import { Cause, Effect, Exit, Layer, Option, Ref, Schema, Scope } from "effect";
import { AcceptedWork } from "../state/accepted-work.ts";
import { parseFlowRecordingRef, recordingInitialLinkFailure } from "./initial-link-model.ts";
import { RecordingInitialLinkIo, RecordingInitialLinks, type RecordingInitialLinkSession } from "./initial-link-service.ts";

export const RecordingInitialLinksLive = (root: Scope.Scope) => Layer.effect(RecordingInitialLinks, Effect.gen(function* () {
  const io = yield* RecordingInitialLinkIo, accepted = yield* AcceptedWork, resources = yield* Scope.Scope;
  const failures = yield* Ref.make<readonly Cause.Reason<never>[]>([]);
  yield* Effect.addFinalizer(() => Ref.get(failures).pipe(Effect.flatMap(reasons => reasons.length ? Effect.failCause(Cause.fromReasons(reasons)) : Effect.void)));
  const open = Effect.gen(function* () {
    const caller = yield* Scope.Scope;
    const alive = () => root.state._tag !== "Closed" && resources.state._tag !== "Closed" && caller.state._tag !== "Closed";
    if (!alive()) return yield* Effect.interrupt;
    const raw = yield* io.parameter;
    const decoded = yield* Schema.decodeUnknownEffect(Schema.NullOr(Schema.String))(raw).pipe(Effect.mapError(recordingInitialLinkFailure));
    if (!alive()) return yield* Effect.interrupt;
    const reference = parseFlowRecordingRef(decoded), consumed = yield* Ref.make(false);
    return {
      reference,
      receive: (ready, notify) => accepted.join(Effect.gen(function* () {
        if (!alive()) return;
        const hydrated = yield* Schema.decodeUnknownEffect(Schema.Boolean)(ready).pipe(Effect.mapError(recordingInitialLinkFailure));
        if (!hydrated || Option.isNone(reference) || (yield* Ref.getAndSet(consumed, true))) return;
        yield* notify;
      }).pipe(Effect.uninterruptible, Effect.onExit(exit => Exit.isFailure(exit) ? Ref.update(failures, current => [
        ...current, ...exit.cause.reasons.flatMap(reason => reason._tag === "Fail" ? Cause.die(reason.error).reasons : [reason])
      ]) : Effect.void)))
    } satisfies RecordingInitialLinkSession;
  });
  return RecordingInitialLinks.of({ open });
}));
